import {
  HydratedUserModel,
  LeadActivityModel,
  LeadCommentModel,
  LeadModel
} from '../models/lead.model';
import {
  LeadActivityUiModel,
  LeadCommentUiModel,
  LeadUiModel,
  SlaState
} from '../ui-models/lead.ui-model';

/**
 * Derives the SLA state the table renders.
 *
 * Four cases rather than a boolean, because "answered late" and "not answered
 * and already late" look the same to a boolean and need different actions: the
 * first is history, the second is someone to chase. `slaMet` is only set once
 * a response exists, so its absence is the signal for the unanswered branch.
 */
function deriveSlaState(lead: LeadModel, now: Date = new Date()): SlaState {
  if (!lead.slaDueAt) return SlaState.UNKNOWN;

  if (lead.firstResponseAt) {
    return lead.slaMet ? SlaState.MET : SlaState.BREACHED;
  }

  const due: number = new Date(lead.slaDueAt).getTime();
  if (now.getTime() > due) return SlaState.BREACHED;

  // At-risk needs the window the target opened, which is due - created.
  const created: number = new Date(lead.createdAt).getTime();
  const target: number = due - created;
  const elapsed: number = now.getTime() - created;
  // 0.75 mirrors SLA_AT_RISK_RATIO in the lead service. The real value is in
  // the config endpoint; this default only applies before it has loaded.
  return target > 0 && elapsed >= target * 0.75 ? SlaState.AT_RISK : SlaState.AWAITING;
}

const toDate = (value?: string): Date | undefined => (value ? new Date(value) : undefined);

/** Hydration fails soft, so a name may be absent and the id is the fallback. */
const nameOf = (user?: HydratedUserModel, id?: string): string | undefined =>
  id ? user?.name ?? user?.email ?? id : undefined;

/**
 * `furnishing-request:24` in words.
 *
 * The key is how a lead joins back to the record another team works from, and
 * it is the only thing on a lead that says it was born from a furnishing
 * request rather than typed in. Rendering it raw would make an operator learn
 * the key format to read their own screen.
 */
function deriveOrigin(idempotencyKey?: string): string | undefined {
  if (!idempotencyKey) return undefined;

  const [kind, reference] = idempotencyKey.split(':');
  if (kind === 'furnishing-request' && reference) return `Furnishing request #${reference}`;
  return idempotencyKey;
}

class LeadMapper {
  public static mapToUI(model: LeadModel): LeadUiModel {
    return {
      id: model.id,
      shortId: model.shortId,
      vertical: model.vertical,
      details: model.details ?? {},
      packageName: model.detailsResolved?.packageName,
      modelName: model.detailsResolved?.modelName,
      modelColor: model.detailsResolved?.modelColor,

      name: model.name ?? '',
      phone: model.phone ?? '',
      email: model.email ?? '',
      message: model.message ?? '',

      source: model.source,
      status: model.status,
      resolutionType: model.resolutionType,
      notQualifiedReason: model.notQualifiedReason,
      notInterestedReason: model.notInterestedReason,
      pendingReason: model.pendingReason,
      resolutionNote: model.resolutionNote,
      resolvedAt: toDate(model.resolvedAt),

      marketingConsent: Boolean(model.marketingConsent),
      consentSource: model.consentSource,
      consentAt: toDate(model.consentAt),

      marketingChannel: model.marketingChannel,
      messagingChannel: model.messagingChannel,
      adType: model.adType,
      campaignSource: model.campaign_source,
      utmSource: model.utm_source,
      utmMedium: model.utm_medium,
      utmCampaign: model.utm_campaign,
      utmCampaignId: model.utm_campaign_id,
      utmTerm: model.utm_term,
      utmContent: model.utm_content,
      fbclid: model.fbclid,
      gclid: model.gclid,
      ttclid: model.ttclid,
      landingPath: model.landingPath,
      referrer: model.referrer,
      // Epoch millis on the wire, not an ISO string like every other date here.
      attributedAt: model.attributedAt ? new Date(model.attributedAt) : undefined,
      firstTouchChannel: model.firstTouch?.marketingChannel,
      firstTouchCampaign: model.firstTouch?.utm_campaign,
      firstTouchSource: model.firstTouch?.utm_source,
      firstTouchMedium: model.firstTouch?.utm_medium,
      firstTouchLandingPath: model.firstTouch?.landingPath,
      firstTouchReferrer: model.firstTouch?.referrer,
      firstTouchAt: toDate(model.firstTouch?.touchedAt),

      assignedToId: model.assignedTo,
      // Falls back to the raw id rather than blank: hydration fails soft, and a
      // blank assignee column reads as unassigned, which is a different and
      // actionable state.
      assigneeName: nameOf(model.assignedToUser, model.assignedTo),
      assignedAt: toDate(model.assignedAt),
      assignedByName: nameOf(model.assignedByUser, model.assignedBy),

      contactAttempts: model.contactAttempts ?? 0,
      remainingContactAttempts: model.remainingContactAttempts ?? 0,
      maxContactAttempts: model.maxContactAttempts ?? 0,
      contactAttemptLog: (model.contactAttemptLog ?? []).map((attempt) => ({
        attemptNo: attempt.attemptNo,
        at: new Date(attempt.at),
        byName: nameOf(attempt.byUser, attempt.by),
        note: attempt.note
      })),

      slaState: deriveSlaState(model),
      slaDueAt: toDate(model.slaDueAt),
      firstResponseAt: toDate(model.firstResponseAt),

      qualityScore: model.qualityScore,
      valueCategory: model.valueCategory,
      scoredAt: toDate(model.scoredAt),

      duplicateOfLeadId: model.duplicateOfLeadId,
      idempotencyKey: model.idempotencyKey,
      originLabel: deriveOrigin(model.idempotencyKey),

      createdByName: nameOf(model.createdByUser, model.createdBy),
      lastUpdatedByName: nameOf(model.lastUpdatedByUser, model.lastUpdatedBy),

      lastComment: model.lastComment,
      createdAt: new Date(model.createdAt),
      updatedAt: new Date(model.updatedAt)
    };
  }

  public static mapToUIList(models: LeadModel[]): LeadUiModel[] {
    return (models ?? []).map(LeadMapper.mapToUI);
  }

  public static mapActivityToUI(model: LeadActivityModel): LeadActivityUiModel {
    return {
      id: model.id,
      activityType: model.activityType,
      description: model.description,
      actorName: model.actorIdUser?.name ?? model.actorIdUser?.email ?? model.actorId,
      // No actorId at all means the system wrote it. An actorId that simply
      // could not be hydrated is NOT a system action, so the flag keys on the
      // raw id rather than on the resolved user.
      isSystem: !model.actorId,
      createdAt: new Date(model.createdAt)
    };
  }

  public static mapActivityToUIList(models: LeadActivityModel[]): LeadActivityUiModel[] {
    return (models ?? []).map(LeadMapper.mapActivityToUI);
  }

  public static mapCommentToUI(model: LeadCommentModel): LeadCommentUiModel {
    return {
      id: model.id,
      text: model.text,
      authorId: model.authorId,
      authorName: model.authorIdUser?.name ?? model.authorIdUser?.email ?? model.authorId,
      createdAt: new Date(model.createdAt),
      updatedAt: new Date(model.updatedAt)
    };
  }

  public static mapCommentToUIList(models: LeadCommentModel[]): LeadCommentUiModel[] {
    return (models ?? []).map(LeadMapper.mapCommentToUI);
  }
}

export default LeadMapper;
